import { type ComponentPropsWithoutRef, type Ref } from 'react'

import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'

const colorClasses = {
  gray: 'tw:bg-gray-50 tw:text-black',
  muted: 'tw:bg-gray-100 tw:text-gray-600',
  neutral: 'tw:bg-gray-neutral-100 tw:text-black',
  success: 'tw:bg-success-50 tw:text-success-700',
  primary: 'tw:bg-primary-50 tw:text-primary-700',
  info: 'tw:bg-sky-50 tw:text-sky-700',
  blue: 'tw:bg-blue-50 tw:text-blue-600',
  warning: 'tw:bg-warning-50 tw:text-warning-700',
  error: 'tw:bg-error-50 tw:text-error-600',
}

const borderClasses = {
  gray: 'tw:border-gray-200',
  muted: 'tw:border-gray-200',
  neutral: 'tw:border-gray-neutral-200',
  success: 'tw:border-success-200',
  primary: 'tw:border-primary-200',
  info: 'tw:border-sky-200',
  blue: 'tw:border-blue-200',
  warning: 'tw:border-warning-200',
  error: 'tw:border-error-200',
}

const sizeClasses = {
  md: 'tw:font-medium tw:text-xs tw:px-2 tw:py-1',
}

const shapeClasses = {
  pill: 'tw:rounded-full',
  rounded: 'tw:rounded-md',
}

type BadgeProps = ComponentPropsWithoutRef<'span'> & {
  color?: keyof typeof colorClasses
  shape?: keyof typeof shapeClasses
  size?: keyof typeof sizeClasses
  bordered?: boolean
  leadingIcon?: IconTypes
  trailingIcon?: IconTypes
  ref?: Ref<HTMLSpanElement>
}

function Badge({
  children,
  color = 'gray',
  shape = 'pill',
  size = 'md',
  bordered = false,
  leadingIcon,
  trailingIcon,
  className,
  ref,
  ...props
}: BadgeProps) {
  return (
    <span
      ref={ref}
      {...props}
      className={cn(
        'tw:inline-flex tw:w-fit tw:items-center tw:justify-center tw:gap-1',
        shapeClasses[shape],
        sizeClasses[size],
        colorClasses[color],
        bordered && cn('tw:border', borderClasses[color]),
        className
      )}
    >
      {leadingIcon && <Icon type={leadingIcon} width={14} height={14} />}
      {children}
      {trailingIcon && <Icon type={trailingIcon} width={14} height={14} />}
    </span>
  )
}

export default Badge
